namespace $ {

	export type $easyreels_user_recorder_phase =
		| 'idle'
		| 'starting'
		| 'recording'
		| 'stopping'
		| 'uploading'
		| 'finishing'
		| 'assembling'
		| 'ready'
		| 'failed'
		| 'limit'
		| 'error'

	export type $easyreels_user_recorder_result = NonNullable<
		ReturnType<typeof $easyreels_user_recorder_recording>['recording']
	>['segments']['nodes'][number]

	export class $easyreels_user_recorder_rejected extends $easyreels_i18n_error {
		constructor(message: string) {
			super(message, $easyreels_i18n_server(message))
		}
	}

	type Segment = {
		index: number
		capture: CaptureInput
		part_start: number
		has_parts: boolean
	}

	const part_ms = 5000
	const max_ms = 60 * 60 * 1000
	const video_bits_per_second = 1_500_000
	const boundary_debounce_ms = 500
	const poll_ms = 3000
	const finish_attempts = 8
	const mimes = ['video/webm;codecs=vp9,opus', 'video/webm;codecs=vp8,opus', 'video/webm', 'video/mp4']

	const say = (key: string, params?: () => $easyreels_i18n_params, detail = '') => $easyreels_i18n_say(`$easyreels_user_recorder_${key}`, params, detail)

	const because = (key: string, error: unknown) => $easyreels_i18n_because(`$easyreels_user_recorder_${key}`, error)

	function sleep(ms: number) {
		return new Promise<void>(resolve => setTimeout(resolve, ms))
	}

	export class $easyreels_user_recorder_session extends $mol_object {

		media = this.make_media()
		uploader = this.make_uploader()
		recorder: MediaRecorder | null = null
		segment: Segment | null = null
		segment_count = 0
		segment_stop: Promise<void> | null = null
		boundary_timer = 0
		tick_timer = 0
		started = 0
		stopping = false
		switch_generation = 0
		closed = false

		@$mol_mem
		phase(next?: $easyreels_user_recorder_phase) {
			$mol_wire_solid()
			return next ?? 'idle'
		}

		@$mol_mem
		busy(next?: boolean) {
			$mol_wire_solid()
			return next ?? false
		}

		@$mol_mem
		switching(next?: boolean) {
			$mol_wire_solid()
			return next ?? false
		}

		@$mol_mem
		status(next?: $easyreels_i18n_note | null) {
			$mol_wire_solid()
			return next ?? null
		}

		@$mol_mem
		error(next?: $easyreels_i18n_note | null) {
			$mol_wire_solid()
			return next ?? null
		}

		@$mol_mem
		limit(next?: $easyreels_i18n_note | null) {
			$mol_wire_solid()
			return next ?? null
		}

		@$mol_mem
		elapsed(next?: number) {
			$mol_wire_solid()
			return next ?? 0
		}

		@$mol_mem
		recording_id(next?: number) {
			$mol_wire_solid()
			return next ?? 0
		}

		@$mol_mem
		retryable(next?: boolean) {
			$mol_wire_solid()
			return next ?? false
		}

		@$mol_mem
		results(next?: readonly $easyreels_user_recorder_result[]) {
			$mol_wire_solid()
			return next ?? []
		}

		frame() {
			return { width: 0, height: 0 }
		}

		async present(_stream: MediaStream) {}

		make_media() {
			const session = this

			return new (class extends $easyreels_user_recorder_media {
				override camera_ended(track: MediaStreamTrack) {
					session.camera_ended(track)
				}
			})()
		}

		make_uploader() {
			const session = this

			return new (class extends $easyreels_user_recorder_uploader {
				override fatal(error: Error) {
					session.abort(error)
				}

				override limit_reached(quota: $easyreels_user_recorder_quota) {
					session.limit_hit(quota)
				}
			})()
		}

		elapsed_ms() {
			return Math.round(performance.now() - this.started)
		}

		idle() {
			return ['idle', 'ready', 'failed', 'limit', 'error'].includes(this.phase())
		}

		warn_unload = (event: BeforeUnloadEvent) => {
			event.preventDefault()
			event.returnValue = ''
		}

		boundary_listener = () => this.orientation_changed()

		preview() {
			if (!this.idle() || this.busy()) return
			this.media.ensure_audio()
			void this.open_preview()
		}

		async open_preview() {
			this.busy(true)
			this.error(null)
			try {
				await this.media.open_preview()
			} catch (error) {
				this.error(because('preview_failed', error))
			} finally {
				this.busy(false)
			}
		}

		switch_camera() {
			const facing = this.media.facing() === 'environment' ? 'user' : 'environment'
			if (this.phase() === 'recording') {
				void this.switch_to(facing)
				return
			}

			if (!this.idle() || this.busy()) return
			if (!this.media.ready()) {
				this.preview()
				return
			}

			void this.switch_preview()
		}

		async switch_preview(device = '') {
			this.busy(true)
			try {
				await this.media.switch_preview(device)
			} catch (error) {
				this.error(because('switch_failed', error))
			} finally {
				this.busy(false)
			}
		}

		choose_camera(device: string) {
			if (!device || device === this.media.camera_id()) return

			const recording = this.phase() === 'recording'
			if (recording ? this.switching() : !this.idle() || this.busy() || !this.media.ready()) return

			this.media.choice('videoinput', device)
			if (recording) void this.switch_to('', device)
			else void this.switch_preview(device)
		}

		choose_mic(device: string) {
			if (!device || device === this.media.mic_id()) return
			if (!this.idle() || this.busy() || !this.media.ready()) return
			this.media.choice('audioinput', device)
			void this.switch_mic(device)
		}

		async switch_mic(device: string) {
			this.busy(true)
			try {
				await this.media.switch_mic(device)
			} catch (error) {
				this.error(because('mic_switch_failed', error))
			} finally {
				this.busy(false)
			}
		}

		start() {
			if (!this.idle() || this.busy()) return
			this.media.ensure_audio()
			void this.begin_recording()
		}

		reset() {
			this.uploader.destructor()
			this.uploader = this.make_uploader()
			this.recorder = null
			this.segment = null
			this.segment_count = 0
			this.stopping = false
			this.switch_generation++
			this.error(null)
			this.limit(null)
			this.status(null)
			this.elapsed(0)
			this.recording_id(0)
			this.retryable(false)
			this.results([])
		}

		async begin_recording() {
			this.reset()
			this.phase('starting')

			try {
				if (this.media.ready()) this.media.meter_start()
				else await this.media.open_preview()

				this.media.mic?.getAudioTracks()[0]?.addEventListener('ended', () => {
					if (!this.stopping) this.error(say('mic_stopped'))
				}, { once: true })

				const answer = (await $mol_wire_async(() => $easyreels_user_recorder_start())()).startRecording
				if (answer.__typename === 'QuotaExceededPayload') {
					this.limit_hit_note(answer)
					this.phase('limit')
					return
				}
				if (answer.__typename === 'ErrorPayload') throw new $easyreels_user_recorder_rejected(answer.message)

				await this.present(this.media.camera()!)
				this.recording_id(answer.recording.id)
				this.uploader.begin(answer.recording.id, answer.tickets)
				this.started = performance.now()
				this.phase('recording')
				this.begin_segment()
				this.tick_timer = setInterval(() => this.tick(), 1000) as unknown as number
				addEventListener('beforeunload', this.warn_unload)
				screen.orientation?.addEventListener('change', this.boundary_listener)
			} catch (error) {
				this.error($easyreels_i18n_error_note(error))
				this.phase('error')
				this.media.release()
			}
		}

		tick() {
			this.elapsed(Math.floor(this.elapsed_ms() / 1000))
			if (this.elapsed_ms() >= max_ms) void this.stop()
		}

		capture(mime: string): CaptureInput {
			const video = this.media.camera()!.getVideoTracks()[0]
			const settings = video.getSettings()
			const audio = this.media.mic?.getAudioTracks()[0]?.getSettings()
			const frame = this.frame()
			const width = frame.width || settings.width || 0
			const height = frame.height || settings.height || 0
			const orientation = width === height ? Orientation.SQUARE : width > height ? Orientation.LANDSCAPE : Orientation.PORTRAIT

			return Object.freeze({
				facingMode: $easyreels_user_recorder_facing_of(video),
				width,
				height,
				orientation,
				mime,
				videoDeviceId: settings.deviceId ?? '',
				audioDeviceId: audio?.deviceId ?? '',
			})
		}

		begin_segment() {
			if (this.stopping || this.phase() !== 'recording') return

			const mime = mimes.find(mime => MediaRecorder.isTypeSupported(mime)) ?? ''
			const options: MediaRecorderOptions = { videoBitsPerSecond: video_bits_per_second }
			if (mime) options.mimeType = mime

			const recorder = new MediaRecorder(this.media.combined(), options)
			const segment: Segment = {
				index: this.segment_count++,
				capture: this.capture(recorder.mimeType || mime || 'video/webm'),
				part_start: this.elapsed_ms(),
				has_parts: false,
			}

			recorder.addEventListener('dataavailable', event => {
				if (event.data.size) this.chunk(event.data, segment)
			})
			recorder.addEventListener('error', event => this.error(say('recorder_failed', undefined, (event as ErrorEvent).message ?? '')))
			recorder.start(part_ms)

			this.recorder = recorder
			this.segment = segment
		}

		chunk(blob: Blob, segment: Segment) {
			segment.has_parts = true
			const end = Math.max(segment.part_start, this.elapsed_ms())
			this.uploader.add({ blob, segment: segment.index, start_ms: segment.part_start, end_ms: end, capture: segment.capture })
			segment.part_start = end
		}

		end_segment() {
			if (this.segment_stop) return this.segment_stop

			const recorder = this.recorder
			const segment = this.segment
			if (!recorder || !segment || recorder.state === 'inactive') return Promise.resolve()

			this.recorder = null
			const stopped = new Promise<void>(resolve => {
				recorder.addEventListener('stop', () => resolve(), { once: true })
				recorder.stop()
			})
				.then(() => {
					if (!segment.has_parts && segment.index === this.segment_count - 1) this.segment_count--
				})
				.finally(() => {
					this.segment_stop = null
				})

			this.segment_stop = stopped
			return stopped
		}

		orientation_changed() {
			clearTimeout(this.boundary_timer)
			this.boundary_timer = setTimeout(() => void this.boundary(), boundary_debounce_ms) as unknown as number
		}

		async boundary() {
			const segment = this.segment
			if (this.phase() !== 'recording' || this.stopping || this.switching() || !segment) return

			const now = this.capture(segment.capture.mime)
			if (now.width === segment.capture.width && now.height === segment.capture.height) return

			this.switching(true)
			try {
				await this.end_segment()
				this.begin_segment()
			} catch (error) {
				this.error(because('boundary_failed', error))
			} finally {
				this.switching(false)
			}
		}

		async show(stream: MediaStream) {
			this.media.show(stream)
			await this.present(stream)
		}

		async switch_to(facing: $easyreels_user_recorder_facing, device = '') {
			if (this.switching() || this.phase() !== 'recording') return

			const generation = ++this.switch_generation
			const current = () => generation === this.switch_generation && this.phase() === 'recording' && !this.stopping
			const old = this.media.camera()
			const old_track = old?.getVideoTracks()[0]
			const old_device = old_track?.getSettings().deviceId ?? ''
			const old_facing = $easyreels_user_recorder_facing_of(old_track)

			this.switching(true)
			this.status(say('switching'))

			let next: MediaStream | null = null
			let released = false

			try {
				await this.end_segment()
				if (!current()) return

				try {
					next = await this.media.acquire(facing, device)
				} catch {
					if (!current()) return
					$easyreels_user_recorder_stop_tracks(old)
					released = true
					next = await this.media.acquire(facing, device)
				}
			} catch (error) {
				this.error(because('camera_switch_failed', error))
				if (!released && $easyreels_user_recorder_live(old)) next = old
				else if (current()) {
					try {
						next = await this.media.acquire(old_facing, old_device)
					} catch (restore) {
						this.error(because('camera_missing', restore))
					}
				}
			} finally {
				this.switching(false)
				this.status(null)

				if (!current()) {
					if (next !== old) $easyreels_user_recorder_stop_tracks(next)
				} else if (next) {
					if (next !== old && !released) $easyreels_user_recorder_stop_tracks(old)
					try {
						await this.show(next)
						this.begin_segment()
					} catch (error) {
						this.error($easyreels_i18n_error_note(error))
					}
				}
			}
		}

		camera_ended(track: MediaStreamTrack) {
			if (this.phase() !== 'recording' || this.stopping || this.switching()) return
			if (this.media.camera()?.getVideoTracks()[0] !== track) return

			this.error(say('camera_taken'))
			void this.switch_to($easyreels_user_recorder_facing_of(track) || 'user')
		}

		limit_hit_note(quota: $easyreels_user_recorder_quota) {
			this.limit(say('limit_detail', () => ({
				used: $easyreels_i18n_bytes(quota.usedBytes),
				limit: $easyreels_i18n_bytes(quota.limitBytes),
			})))
		}

		limit_hit(quota: $easyreels_user_recorder_quota) {
			this.limit_hit_note(quota)
			void this.stop()
		}

		async stop() {
			if (this.stopping || this.phase() !== 'recording') return

			this.stopping = true
			this.switch_generation++
			this.end_timers()
			this.phase('stopping')

			try {
				await this.end_segment()
				this.media.release()
				this.phase('uploading')
				await this.uploader.drain()
				await this.finish()
			} catch (error) {
				if (this.phase() === 'failed') return
				this.error($easyreels_i18n_error_note(error))
				this.retryable(!(error instanceof $easyreels_user_recorder_rejected))
				this.phase('error')
			}
		}

		retry() {
			if (!this.retryable()) return
			this.retryable(false)
			void this.finish().catch(error => {
				this.error($easyreels_i18n_error_note(error))
				this.retryable(!(error instanceof $easyreels_user_recorder_rejected))
				this.phase('error')
			})
		}

		async finish() {
			this.phase('finishing')
			const input = { recordingId: this.recording_id(), streams: [{ index: this.uploader.stream, parts: this.uploader.manifest() }] }

			for (let attempt = 0; ; attempt++) {
				try {
					const answer = (await $mol_wire_async(() => $easyreels_user_recorder_finish({ input }))()).finishRecording
					if (answer.__typename === 'ErrorPayload') throw new $easyreels_user_recorder_rejected(answer.message)
					break
				} catch (error) {
					if (error instanceof $easyreels_user_recorder_rejected || attempt >= finish_attempts - 1) throw error
					this.error(say('waiting_server'))
					await sleep(Math.min(30_000, 1000 * 2 ** attempt))
				}
			}

			removeEventListener('beforeunload', this.warn_unload)
			this.error(null)
			this.phase('assembling')
			await this.poll()
		}

		async poll() {
			const id = this.recording_id()

			while (!this.closed) {
				await sleep(poll_ms)

				let recording: ReturnType<typeof $easyreels_user_recorder_recording>['recording']
				try {
					recording = (await $mol_wire_async(() => $easyreels_user_recorder_recording({ id }, { revalidate: false }))()).recording
				} catch {
					continue
				}

				if (!recording) throw new $easyreels_user_recorder_rejected('recording not found')
				if (recording.status === RecordingStatus.READY) {
					this.results(recording.segments.nodes)
					this.phase('ready')
					return
				}
				if (recording.status === RecordingStatus.FAILED) {
					this.error($easyreels_i18n_server(recording.error))
					this.phase('failed')
					return
				}
			}
		}

		abort(error: Error) {
			this.stopping = true
			this.switch_generation++
			this.end_timers()
			const recorder = this.recorder
			this.recorder = null
			if (recorder && recorder.state !== 'inactive') recorder.stop()
			this.media.release()
			removeEventListener('beforeunload', this.warn_unload)
			this.error($easyreels_i18n_error_note(error))
			this.phase('failed')
		}

		again() {
			if (!this.idle()) return
			this.reset()
			this.phase('idle')
		}

		end_timers() {
			clearTimeout(this.boundary_timer)
			clearInterval(this.tick_timer)
			screen.orientation?.removeEventListener('change', this.boundary_listener)
		}

		override destructor() {
			this.closed = true
			this.switch_generation++
			this.end_timers()
			removeEventListener('beforeunload', this.warn_unload)
			this.recorder?.stop()
			this.uploader.destructor()
			this.media.destructor()
		}

	}

}
